import React, { useState, useEffect, useRef } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, PiggyBank, FileBarChart, ArrowUpRight, ArrowDownRight, X, Download, Plus, ScanLine, Paperclip, Loader2, Pencil, Trash2, Upload, ChevronDown, RefreshCw, Filter, Receipt, Repeat, FileSpreadsheet, Mail, Check, AlertCircle, Landmark, Banknote, Search, Eye, EyeOff, Settings2, GripVertical } from "lucide-react";
import api from "../../lib/api";
import { FinancesGuide } from "../../components/guides/FinancesGuide";
import { AnnualReportButton } from "../../components/brand/AnnualReportButton";
import { useCachedList } from "../../lib/useCachedList";
import { toast } from "sonner";
import ImportHistoryModal from "../../components/dashboard/ImportHistoryModal";

function PayoutBanner() {
  const [n, setN] = useState(0);
  const navigate = useNavigate();
  useEffect(() => { api.get("/str/payouts/count").then((r) => setN(r.data.count)).catch(() => {}); }, []);
  if (!n) return null;
  return (
    <button onClick={() => navigate("/app/saisonnier/versements")} className="w-full flex items-center gap-3 rounded-2xl bg-brand-lime/25 border border-brand-lime px-5 py-3 text-left text-sm text-brand-greenDeep hover:bg-brand-lime/40 transition-colors" data-testid="fin-payout-banner">
      <Banknote className="w-5 h-5 shrink-0" /><span className="flex-1"><b>{n} versement(s) Airbnb / Booking / Abritel</b> à associer à leurs réservations saisonnières.</span><span className="font-semibold">Valider →</span>
    </button>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);
const NO_PROPERTY = "Pas lié à un bien";
const NO_LEASE = "Pas lié à une location";
const STATUS_CLS = {
  "Payé": "bg-brand-lime/30 text-brand-greenDeep",
  "En attente": "bg-amber-100 text-amber-700",
  "Partiel": "bg-blue-100 text-blue-700",
  "Perdu": "bg-red-100 text-red-700",
  "Non payé": "bg-red-50 text-red-600",
};

const FIN_SECTIONS = [
  { key: "kpi", label: "Indicateurs clés" },
  { key: "categories", label: "Montants par catégorie" },
  { key: "fiscal_aid", label: "Aide à la déclaration" },
  { key: "fiscal", label: "Tableau de bord par bien" },
  { key: "transactions", label: "Dernières transactions" },
];
const FIN_LABELS = FIN_SECTIONS.reduce((a, s) => { a[s.key] = s.label; return a; }, {});

function FinDraggable({ blockKey, hidden, onToggle, children }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={blockKey} dragListener={false} dragControls={controls} className="bg-brand-softer/40 rounded-2xl p-2">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2">
          <button onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing text-brand-green/40 hover:text-brand-greenDeep touch-none p-1" data-testid={`fin-drag-${blockKey}`}>
            <GripVertical className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-brand-greenDeep">{FIN_LABELS[blockKey]}</span>
        </div>
        <button onClick={onToggle} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${hidden ? "bg-brand-soft text-brand-green/60" : "bg-brand-lime/30 text-brand-greenDeep"}`} data-testid={`fin-toggle-${blockKey}`}>
          {hidden ? <><EyeOff className="w-3.5 h-3.5" /> Masqué</> : <><Eye className="w-3.5 h-3.5" /> Affiché</>}
        </button>
      </div>
      <div className={hidden ? "opacity-40 pointer-events-none" : ""}>{children}</div>
    </Reorder.Item>
  );
}

const TX_PAGE = 100;
const norm = (v) => String(v ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const amountKeys = (a) => { const n = Number(a) || 0; return `${n} ${n.toFixed(2)} ${n.toFixed(2).replace(".", ",")} ${String(n).replace(".", ",")}`; };

function Hl({ text, q }) {
  const str = String(text ?? "");
  if (!q || !str) return str;
  const parts = [...str].map((c) => norm(c));
  const flat = parts.join("");
  const idx = flat.indexOf(q);
  if (idx < 0) return str;
  let acc = 0, start = -1, end = -1;
  parts.forEach((p, i) => { if (start < 0 && acc + p.length > idx) start = i; if (end < 0 && acc + p.length >= idx + q.length) end = i + 1; acc += p.length; });
  const chars = [...str];
  return <>{chars.slice(0, start).join("")}<mark className="bg-brand-lime/60 text-inherit rounded px-0.5" data-testid="search-hl">{chars.slice(start, end).join("")}</mark>{chars.slice(end).join("")}</>;
}

export default function Finances() {
  const FILTERS_KEY = "finances_filters_v1";
  const savedFilters = (() => {
    try { return JSON.parse(localStorage.getItem(FILTERS_KEY) || "{}") || {}; } catch { return {}; }
  })();
  const [transactions, setTransactions, txLoaded, loadTx] = useCachedList("/transactions");
  const [fiscalOpen, setFiscalOpen] = useState(false);
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [leases, setLeases] = useState([]);
  const [cats, setCats] = useState({ expense: [], revenue: [] });
  const [txModal, setTxModal] = useState(null);
  const [addMenu, setAddMenu] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [multiScan, setMultiScan] = useState(null); // {rows:[...]}
  const [fBien, setFBien] = useState(savedFilters.fBien || "");
  const [fCat, setFCat] = useState(savedFilters.fCat || "");
  const [fFrom, setFFrom] = useState(savedFilters.fFrom || "");
  const [fTo, setFTo] = useState(savedFilters.fTo || "");
  const [fPreset, setFPreset] = useState(savedFilters.fPreset || "");
  const [fStatus, setFStatus] = useState(savedFilters.fStatus || "");
  const [fRecon, setFRecon] = useState(savedFilters.fRecon || "");
  const [fSearch, setFSearch] = useState(savedFilters.fSearch || "");
  const [fType, setFType] = useState(savedFilters.fType || "");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [partialTx, setPartialTx] = useState(null);
  const [selectedTx, setSelectedTx] = useState([]);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [tab, setTab] = useState("all"); // "all" | "arrears"
  const [remindingId, setRemindingId] = useState(null);
  const [finHidden, setFinHidden] = useState([]);
  const [finOrder, setFinOrder] = useState(FIN_SECTIONS.map((s) => s.key));
  const [finEdit, setFinEdit] = useState(false);
  const [savingFinPrefs, setSavingFinPrefs] = useState(false);
  const scanRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const openedRef = useRef(false);

  useEffect(() => {
    api.get("/owners").then((r) => setOwners(r.data || [])).catch(() => {});
    api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
    api.get("/leases").then((r) => setLeases(r.data || [])).catch(() => {});
    api.get("/transactions/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/finances/preferences").then((r) => {
      setFinHidden(r.data?.hidden || []);
      if (r.data?.order?.length) setFinOrder(r.data.order);
    }).catch(() => {});
  }, []);

  const toggleFinSection = (k) => setFinHidden((h) => h.includes(k) ? h.filter((x) => x !== k) : [...h, k]);
  const saveFinPrefs = async () => {
    setSavingFinPrefs(true);
    try {
      await api.put("/finances/preferences", { hidden: finHidden, order: finOrder });
      toast.success("Affichage de la page enregistré");
      setFinEdit(false);
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSavingFinPrefs(false); }
  };

  const renderFinSection = (key) => {
    if (key === "kpi") return (
      <div className="grid sm:grid-cols-3 gap-6">
        {[
          { label: "Revenus fonciers", value: fIncome, icon: TrendingUp, accent: "bg-brand-lime/30 text-brand-greenDeep" },
          { label: "Dépenses", value: fExpense, icon: TrendingDown, accent: "bg-orange-50 text-orange-600" },
          { label: "Résultat net", value: fNet, icon: PiggyBank, accent: "bg-brand-emerald/10 text-brand-emerald" },
        ].map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid={`kpi-${c.label === "Revenus fonciers" ? "revenus" : c.label === "Dépenses" ? "depenses" : "net"}`}>
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.accent} mb-4`}><Icon className="w-5 h-5" /></span>
              <p className="text-3xl font-bold text-brand-greenDeep">{eur(c.value)} €</p>
              <p className="text-sm text-brand-green/60 mt-1">{c.label}</p>
            </div>
          );
        })}
      </div>
    );
    if (key === "categories") return (
      <div className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid="category-breakdown">
        <h3 className="font-semibold text-lg text-brand-greenDeep mb-4">Montants par catégorie</h3>
        {catBreakdown.length === 0 ? (
          <p className="text-sm text-brand-green/50 py-6 text-center">Aucune écriture pour ces filtres.</p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {catBreakdown.map((c) => (
              <div key={`${c.type}-${c.category}`} className="flex items-center justify-between py-2 px-3 rounded-xl hover:bg-brand-softer/50" data-testid={`cat-row-${c.category}`}>
                <span className="flex items-center gap-2 text-sm text-brand-greenDeep">
                  <span className={`w-2.5 h-2.5 rounded-full ${c.type === "Revenu" ? "bg-brand-lime" : "bg-orange-400"}`} />
                  {c.category}<span className="text-brand-green/40 text-xs">· {c.type}</span>
                </span>
                <span className={`font-semibold text-sm ${c.type === "Revenu" ? "text-brand-greenDeep" : "text-orange-600"}`}>{c.type === "Revenu" ? "+" : "-"}{eur(c.total)} €</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
    if (key === "fiscal_aid") return (
      <div className="bg-brand-greenDeep rounded-2xl p-6 text-white">
        <h3 className="font-semibold text-lg mb-2">Aide à la déclaration</h3>
        <p className="text-sm text-white/70 mb-6">Selon les filtres sélectionnés (déclaration 2044).</p>
        <div className="space-y-3 text-sm">
          {[["Revenus bruts", `${eur(fIncome)} €`], ["Charges déductibles", `${eur(fExpense)} €`], ["Revenu net imposable", `${eur(fNet)} €`]].map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-white/10 pb-3">
              <span className="text-white/70">{k}</span>
              <span className="font-semibold">{v}</span>
            </div>
          ))}
        </div>
        <button onClick={() => setFiscalOpen(true)} data-testid="export-2044-btn" className="mt-6 w-full py-3 rounded-full bg-brand-lime text-brand-greenDeep font-semibold hover:bg-brand-limeDark transition-colors">Attestation fiscale (2044)</button>
      </div>
    );
    if (key === "fiscal") return (
      <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden" data-testid="fiscal-dashboard">
        <div className="px-6 py-4 border-b border-brand-soft flex items-center justify-between">
          <h3 className="font-semibold text-brand-greenDeep flex items-center gap-2"><FileBarChart className="w-4 h-4" /> Tableau de bord par bien</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => window.open(`${BACKEND_URL}/api/finances/accountant-export?year=${new Date().getFullYear()}`, "_blank")} data-testid="accountant-export-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep font-semibold text-xs transition-colors">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Export comptable (Excel)
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-brand-green/50 border-b border-brand-soft">
                <th className="px-6 py-3 font-medium">Bien</th>
                <th className="px-4 py-3 font-medium text-right">Revenus</th>
                <th className="px-4 py-3 font-medium text-right">Dépenses</th>
                <th className="px-6 py-3 font-medium text-right">Net</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-soft">
              {bienBreakdown.length === 0 && <tr><td colSpan="4" className="px-6 py-8 text-center text-brand-green/50">Aucune donnée pour ces filtres.</td></tr>}
              {bienBreakdown.map((r) => (
                <tr key={r.property} className="hover:bg-brand-softer/40" data-testid={`fiscal-row-${r.property}`}>
                  <td className="px-6 py-3 font-medium text-brand-greenDeep">{r.property}</td>
                  <td className="px-4 py-3 text-right text-brand-greenDeep">{eur(r.revenus)} €</td>
                  <td className="px-4 py-3 text-right text-orange-600">{eur(r.depenses)} €</td>
                  <td className={`px-6 py-3 text-right font-semibold ${r.net >= 0 ? "text-brand-greenDeep" : "text-orange-600"}`}>{eur(r.net)} €</td>
                </tr>
              ))}
            </tbody>
            {bienBreakdown.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-brand-soft bg-brand-softer/40 font-semibold text-brand-greenDeep" data-testid="fiscal-totals">
                  <td className="px-6 py-3">Total</td>
                  <td className="px-4 py-3 text-right">{eur(fIncome)} €</td>
                  <td className="px-4 py-3 text-right text-orange-600">{eur(fExpense)} €</td>
                  <td className={`px-6 py-3 text-right ${fNet >= 0 ? "text-brand-greenDeep" : "text-orange-600"}`}>{eur(fNet)} €</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
    if (key === "transactions") return (
      <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden">
        <div className="px-6 pt-4 pb-3 border-b border-brand-soft flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-1 bg-brand-softer rounded-full p-1" data-testid="finance-tabs">
              <button onClick={() => setTab("all")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === "all" ? "bg-white text-brand-greenDeep shadow-sm" : "text-brand-green/55 hover:text-brand-greenDeep"}`} data-testid="tab-all">Toutes</button>
              <button onClick={() => setTab("arrears")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${tab === "arrears" ? "bg-white text-brand-greenDeep shadow-sm" : "text-brand-green/55 hover:text-brand-greenDeep"}`} data-testid="tab-arrears">
                À encaisser {arrearsCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">{arrearsCount}</span>}
              </button>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-amber-50 border border-amber-100" data-testid="reste-a-encaisser">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
              <div className="text-right">
                <p className="text-[11px] text-amber-700/80 leading-none">Reste à encaisser</p>
                <p className="text-base font-bold text-amber-700 leading-tight" data-testid="reste-a-encaisser-amount">{resteAEncaisser.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between">
            <h3 className="font-semibold text-brand-greenDeep shrink-0">Dernières transactions</h3>
            <div className="flex flex-wrap items-center gap-2" data-testid="finance-filters">
            {selectedTx.length > 0 && (
              <button onClick={() => setConfirmBulk(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 font-semibold text-xs transition-colors" data-testid="bulk-delete-btn"><Trash2 className="w-3.5 h-3.5" /> Supprimer la sélection ({selectedTx.length})</button>
            )}
            <button onClick={exportCsv} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep font-semibold text-xs transition-colors" data-testid="export-csv-btn"><Download className="w-3.5 h-3.5" /> CSV</button>
          </div>
          </div>
        </div>
        <div className="divide-y divide-brand-soft">
          {filtered.length === 0 && <p className="px-6 py-8 text-center text-sm text-brand-green/50" data-testid="tx-empty">{txLoaded ? "Aucune écriture. Utilisez « Ajouter » pour créer un revenu ou une dépense." : <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement des écritures…</span>}</p>}
          {filtered.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-auto">
                <thead>
                  <tr className="text-left text-brand-green/50 border-b border-brand-soft">
                    <th className="px-4 py-3 font-medium w-10">
                      <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelAllTx} data-testid="select-all-tx" className="w-4 h-4 accent-red-500 cursor-pointer" title="Tout sélectionner" />
                    </th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap cursor-pointer select-none hover:text-brand-greenDeep" onClick={() => toggleSort("date")} data-testid="sort-date">Date{sortKey === "date" ? (sortDir === "asc" ? " ▲" : " ▼") : ""}</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap cursor-pointer select-none hover:text-brand-greenDeep" onClick={() => toggleSort("property")} data-testid="sort-property">Bien{sortKey === "property" ? (sortDir === "asc" ? " ▲" : " ▼") : ""}</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap cursor-pointer select-none hover:text-brand-greenDeep" onClick={() => toggleSort("tenant")} data-testid="sort-tenant">Locataire{sortKey === "tenant" ? (sortDir === "asc" ? " ▲" : " ▼") : ""}</th>
                    <th className="px-4 py-3 font-medium text-right whitespace-nowrap cursor-pointer select-none hover:text-brand-greenDeep" onClick={() => toggleSort("amount")} data-testid="sort-amount">Montant{sortKey === "amount" ? (sortDir === "asc" ? " ▲" : " ▼") : ""}</th>
                    <th className="px-4 py-3 font-medium">Description</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap cursor-pointer select-none hover:text-brand-greenDeep" onClick={() => toggleSort("status")} data-testid="sort-status">Statut{sortKey === "status" ? (sortDir === "asc" ? " ▲" : " ▼") : ""}</th>
                    <th className="px-4 py-3 font-medium text-center whitespace-nowrap cursor-pointer select-none hover:text-brand-greenDeep" onClick={() => toggleSort("reconciled")} data-testid="sort-reconciled">Écriture rapprochée{sortKey === "reconciled" ? (sortDir === "asc" ? " ▲" : " ▼") : ""}</th>
                    <th className="px-4 py-3 font-medium text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-soft">
                  {filtered.slice(0, visibleTx).map((t) => {
                    const isQuittance = t.source === "quittance";
                    const locked = isQuittance && t.status === "Payé";
                    const unpaidQuittance = isQuittance && t.status !== "Payé";
                    const auto = t.source && t.source !== "manuel";
                    const isPartial = t.status === "Partiel";
                    const canRemind = t.type === "Revenu" && (t.status === "En attente" || t.status === "Partiel");
                    return (
                      <tr key={t.id} className={`hover:bg-brand-softer/40 group align-top ${selSet.has(t.id) ? "bg-red-50/40" : ""}`} data-testid={`tx-row-${t.id}`}>
                        <td className="px-4 py-3">
                          <input type="checkbox" checked={selSet.has(t.id)} onChange={() => toggleSelTx(t.id)} data-testid={`select-tx-${t.id}`} className="w-4 h-4 accent-red-500 cursor-pointer" />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-brand-green/70">{t.date ? new Date(t.date).toLocaleDateString("fr-FR") : "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-brand-greenDeep">{t.property && t.property !== "Pas lié à un bien" ? <Hl text={canonProp(t.property)} q={qSearch} /> : "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-brand-green/70">{canonTenant(t.tenant) ? <Hl text={canonTenant(t.tenant)} q={qSearch} /> : "—"}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <span className={`font-semibold ${t.type === "Revenu" ? "text-brand-greenDeep" : "text-orange-600"}`}>{t.type === "Revenu" ? "+" : "-"}{(t.amount || 0).toLocaleString("fr-FR")} €</span>
                          {isPartial && (
                            <div className="mt-1 w-28 ml-auto" data-testid={`tx-progress-${t.id}`}>
                              <div className="h-1.5 rounded-full bg-brand-soft overflow-hidden"><div className="h-full bg-brand-limeDark" style={{ width: `${Math.min(100, ((t.paid || 0) / (t.amount || 1)) * 100)}%` }} /></div>
                              <p className="text-[10px] text-brand-green/50 mt-0.5">{(t.paid || 0).toLocaleString("fr-FR")} / {(t.amount || 0).toLocaleString("fr-FR")} €</p>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-brand-greenDeep flex items-center gap-2"><Hl text={t.label} q={qSearch} />
                            {isQuittance && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-brand-softer text-brand-green/60 font-semibold shrink-0" data-testid={`tx-linked-${t.id}`}><Receipt className="w-3 h-3" /> Quittance</span>}
                            {auto && !isQuittance && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-brand-softer text-brand-green/60 font-semibold shrink-0" data-testid={`tx-auto-${t.id}`}><Repeat className="w-3 h-3" /> Auto</span>}
                            {t.document_url && <a href={imgSrc(t.document_url)} target="_blank" rel="noreferrer" className="text-brand-green/40 hover:text-brand-greenDeep shrink-0" title="Document" data-testid={`tx-doc-${t.id}`}><Paperclip className="w-3.5 h-3.5" /></a>}
                          </p>
                          <p className="text-xs text-brand-green/45">{t.category}</p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {locked ? (
                            <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-brand-lime/30 text-brand-greenDeep">Payé</span>
                          ) : (
                            <select value={t.status || "Payé"} onChange={(e) => changeStatus(t, e.target.value)} className={`status-sel ${STATUS_CLS[t.status || "Payé"]}`} data-testid={`tx-status-${t.id}`}>
                              {(unpaidQuittance ? ["Non payé", "Perdu"] : ["Payé", "En attente", "Partiel", "Perdu", "Non payé"]).map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap" data-testid={`tx-reconciled-${t.id}`}>
                          {t.reconciled ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-brand-emerald/10 text-brand-emerald"><Check className="w-3 h-3" /> Oui</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-brand-soft text-brand-green/50">Non</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {canRemind && (
                            <button onClick={() => remind(t)} disabled={remindingId === t.id} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-700 font-semibold mr-2 disabled:opacity-60 transition-colors" data-testid={`tx-remind-${t.id}`}>
                              {remindingId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Relance
                            </button>
                          )}
                          {isPartial && !locked && <button onClick={() => setPartialTx(t)} className="text-xs text-brand-limeDark hover:text-brand-greenDeep font-medium mr-2" data-testid={`tx-addpay-${t.id}`}>+ Versement</button>}
                          <span className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openTx(t.type, { category: t.category, owner: t.owner, property: t.property, lease: t.lease, tenant: t.tenant, amount: t.amount, date: t.date, label: t.label, document_url: t.document_url, status: t.status, reconciled: t.reconciled, unpaid_reason: t.unpaid_reason || "", source: t.source }, t.id)} className="p-1.5 rounded-full hover:bg-brand-soft text-brand-green/50 hover:text-brand-greenDeep" data-testid={`tx-edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => deleteTx(t.id)} className="p-1.5 rounded-full hover:bg-red-50 text-brand-green/50 hover:text-red-600" data-testid={`tx-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length > visibleTx && (
                <div className="px-6 py-4 flex flex-wrap items-center justify-center gap-3 border-t border-brand-soft" data-testid="tx-load-more-box">
                  <span className="text-xs text-brand-green/55" data-testid="tx-visible-count">{visibleTx} sur {filtered.length} écritures affichées (totaux calculés sur toutes)</span>
                  <button onClick={() => setVisibleTx((v) => v + TX_PAGE)} className="px-4 py-2 rounded-full border border-brand-soft text-sm font-semibold text-brand-greenDeep hover:bg-brand-softer transition-colors" data-testid="tx-load-more">Afficher {Math.min(TX_PAGE, filtered.length - visibleTx)} de plus</button>
                </div>
              )}
            </div>
          )}
          {filtered.length > 0 && (
            <div className="px-6 py-4 border-t-2 border-brand-soft bg-brand-softer/40 flex flex-wrap items-center justify-between gap-3" data-testid="filtered-totals">
              <span className="text-xs font-medium text-brand-green/50">{filtered.length} écriture{filtered.length > 1 ? "s" : ""}{filtersActive ? " (filtrées)" : ""}</span>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                <span className="flex items-center gap-2"><span className="text-brand-green/50">Revenus</span><span className="font-bold text-brand-greenDeep" data-testid="filtered-total-revenus">+{filteredTotals.revenus.toLocaleString("fr-FR")} €</span></span>
                <span className="flex items-center gap-2"><span className="text-brand-green/50">Dépenses</span><span className="font-bold text-orange-600" data-testid="filtered-total-depenses">-{filteredTotals.depenses.toLocaleString("fr-FR")} €</span></span>
                <span className="flex items-center gap-2"><span className="text-brand-green/50">Net</span><span className={`font-bold ${filteredTotals.revenus - filteredTotals.depenses >= 0 ? "text-brand-emerald" : "text-red-600"}`} data-testid="filtered-total-net">{(filteredTotals.revenus - filteredTotals.depenses >= 0 ? "+" : "")}{(filteredTotals.revenus - filteredTotals.depenses).toLocaleString("fr-FR")} €</span></span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
    return null;
  };

  // Prefill des filtres depuis l'URL (?from=&to=&property=) — clic sur le graphe du dashboard
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("from")) setFFrom(p.get("from"));
    if (p.get("to")) setFTo(p.get("to"));
    if (p.get("property")) setFBien(p.get("property"));
  }, [location.search]);

  // Conserve les filtres jusqu'au clic sur « Réinitialiser »
  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify({ fBien, fCat, fType, fFrom, fTo, fPreset, fStatus, fRecon, fSearch }));
    } catch { /* ignore */ }
  }, [fBien, fCat, fType, fFrom, fTo, fPreset, fStatus, fRecon, fSearch]);

  // Ouverture depuis une notification de charge récurrente (?tx=&notif=)
  useEffect(() => {
    if (openedRef.current || !transactions.length) return;
    const params = new URLSearchParams(location.search);
    const txId = params.get("tx");
    const notif = params.get("notif");
    if (txId) {
      const t = transactions.find((x) => x.id === txId);
      if (t) {
        openedRef.current = true;
        openTx(t.type, { category: t.category, owner: t.owner, property: t.property, lease: t.lease, amount: t.amount, date: t.date, label: t.label, document_url: t.document_url, _focusDoc: true }, t.id);
        if (notif) api.post(`/notifications/${notif}/read`).catch(() => {});
        navigate("/app/finances", { replace: true });
      }
    }
  }, [transactions, location.search]); // eslint-disable-line

  const openTx = (type, prefill = {}, editId = null) => {
    setAddMenu(false);
    setTxModal({
      editId,
      type: type === "Revenu" ? "Revenu" : "Dépense",
      category: "", owner: "", property: "", lease: "", tenant: "", amount: "", date: new Date().toISOString().slice(0, 10),
      label: "", document_url: "", status: "Payé", unpaid_reason: "", source: "manuel", ...prefill,
    });
  };

  const onScanFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (scanRef.current) scanRef.current.value = "";
    if (!files.length) return;
    setAddMenu(false);
    setScanning(true);
    try {
      const rows = [];
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const res = await api.post("/transactions/scan-invoice", fd, { headers: { "Content-Type": "multipart/form-data" } });
          let doc = "";
          try {
            const fd2 = new FormData();
            fd2.append("file", file);
            const up = await api.post("/tenants/upload-file", fd2, { headers: { "Content-Type": "multipart/form-data" } });
            doc = up.data.url;
          } catch { /* optional */ }
          rows.push({
            type: "Dépense", category: res.data.category || "", owner: "", property: "", lease: "",
            amount: res.data.amount || "", date: res.data.date || new Date().toISOString().slice(0, 10),
            label: res.data.vendor || res.data.description || file.name, document_url: doc, filename: file.name,
          });
        } catch {
          rows.push({ type: "Dépense", category: "", owner: "", property: "", lease: "", amount: "", date: new Date().toISOString().slice(0, 10), label: file.name, document_url: "", filename: file.name, error: true });
        }
      }
      if (rows.length === 1) {
        openTx("Dépense", rows[0]);
        toast.success("Facture analysée — vérifiez les informations");
      } else {
        setMultiScan({ rows });
        toast.success(`${rows.length} factures analysées — relisez avant de créer`);
      }
    } catch (err) {
      toast.error("Analyse impossible");
    } finally { setScanning(false); }
  };

  const reconcile = async () => {
    setReconciling(true);
    try {
      const res = await api.post("/finances/reconcile-rents");
      await loadTx();
      toast.success(res.data.created > 0 ? `${res.data.created} loyer(s) rattaché(s) aux revenus` : "Loyers déjà à jour");
    } catch { toast.error("Erreur"); }
    finally { setReconciling(false); }
  };

  const propCanon = React.useMemo(() => {
    const m = {};
    (properties || []).forEach((p) => { if (p.name) m[p.name.trim().toLowerCase()] = p.name; });
    transactions.forEach((t) => { const n = (t.property || "").trim(); if (n && !m[n.toLowerCase()]) m[n.toLowerCase()] = n; });
    return m;
  }, [properties, transactions]);
  const canonProp = (name) => { const n = (name || "").trim(); return n ? (propCanon[n.toLowerCase()] || n) : n; };
  const tenantCanon = React.useMemo(() => {
    const m = {};
    (leases || []).forEach((l) => { if (l.tenant) m[l.tenant.trim().toLowerCase()] = l.tenant; });
    transactions.forEach((t) => { const n = (t.tenant || "").trim(); if (n && !m[n.toLowerCase()]) m[n.toLowerCase()] = n; });
    return m;
  }, [leases, transactions]);
  const canonTenant = (name) => { const n = (name || "").trim(); return n ? (tenantCanon[n.toLowerCase()] || n) : n; };

  const dSearch = React.useDeferredValue(fSearch);
  const qSearch = norm(dSearch.trim());
  const searchIndex = React.useMemo(() => new Map(transactions.map((t) => [t.id, norm(`${t.label || ""} ${t.category || ""} ${t.tenant || ""} ${t.property || ""} ${canonProp(t.property)} ${canonTenant(t.tenant)} ${amountKeys(t.amount)}`)])), [transactions]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtered = React.useMemo(() => transactions.filter((t) => {
    if (fBien && (t.property || "").trim().toLowerCase() !== fBien.trim().toLowerCase()) return false;
    if (fCat && t.category !== fCat) return false;
    if (fType && t.type !== fType) return false;
    const d = t.date || "";
    if (fFrom && d < fFrom) return false;
    if (fTo && d > fTo) return false;
    if (fStatus && (t.status || "Payé") !== fStatus) return false;
    if (fRecon === "oui" && !t.reconciled) return false;
    if (fRecon === "non" && t.reconciled) return false;
    if (qSearch && !(searchIndex.get(t.id) || "").includes(qSearch)) return false;
    if (tab === "arrears" && !(t.type === "Revenu" && (t.status === "En attente" || t.status === "Partiel"))) return false;
    return true;
  }).sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    let va, vb;
    if (sortKey === "amount") { va = a.amount || 0; vb = b.amount || 0; }
    else if (sortKey === "reconciled") { va = a.reconciled ? 1 : 0; vb = b.reconciled ? 1 : 0; }
    else { va = (a[sortKey] || "").toString().toLowerCase(); vb = (b[sortKey] || "").toString().toLowerCase(); }
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  }), [transactions, searchIndex, fBien, fCat, fType, fFrom, fTo, fStatus, fRecon, qSearch, tab, sortKey, sortDir]);
  const [visibleTx, setVisibleTx] = useState(TX_PAGE);
  useEffect(() => { setVisibleTx(TX_PAGE); }, [fBien, fCat, fType, fFrom, fTo, fStatus, fRecon, qSearch, tab, sortKey, sortDir]);
  const selSet = React.useMemo(() => new Set(selectedTx), [selectedTx]);
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "amount" || sortKey === "date" ? "desc" : "asc"); }
  };
  const eur = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const _real = (t) => (t.status === "Non payé" ? 0 : (t.realized ?? t.amount ?? 0));
  const stats = React.useMemo(() => {
  const filteredTotals = filtered.reduce((acc, t) => {
    if (t.status === "Non payé") return acc;
    if (t.type === "Revenu") acc.revenus += t.amount || 0;
    else acc.depenses += t.amount || 0;
    return acc;
  }, { revenus: 0, depenses: 0 });
  const fIncome = filtered.filter((t) => t.type === "Revenu").reduce((s, t) => s + _real(t), 0);
  const fExpense = filtered.filter((t) => t.type === "Dépense").reduce((s, t) => s + _real(t), 0);
  const fNet = fIncome - fExpense;
  const catBreakdown = (() => {
    const m = {};
    filtered.forEach((t) => {
      if (t.status === "Non payé") return;
      const key = `${t.type}||${t.category || "Sans catégorie"}`;
      m[key] = m[key] || { type: t.type, category: t.category || "Sans catégorie", total: 0 };
      m[key].total += _real(t);
    });
    return Object.values(m).sort((a, b) => b.total - a.total);
  })();
  const bienBreakdown = (() => {
    const m = {};
    filtered.forEach((t) => {
      if (t.status === "Non payé") return;
      const name = t.property && t.property !== "Pas lié à un bien" ? canonProp(t.property) : "Non affecté";
      m[name] = m[name] || { property: name, revenus: 0, depenses: 0 };
      if (t.type === "Revenu") m[name].revenus += _real(t); else m[name].depenses += _real(t);
    });
    return Object.values(m).map((r) => ({ ...r, net: r.revenus - r.depenses })).sort((a, b) => b.net - a.net);
  })();
  return { filteredTotals, fIncome, fExpense, fNet, catBreakdown, bienBreakdown };
  }, [filtered, propCanon]); // eslint-disable-line react-hooks/exhaustive-deps
  const { filteredTotals, fIncome, fExpense, fNet, catBreakdown, bienBreakdown } = stats;
  const filtersActive = fBien || fCat || fType || fFrom || fTo || fStatus || fRecon || fSearch;
  const clearFilters = () => { setFBien(""); setFCat(""); setFType(""); setFFrom(""); setFTo(""); setFStatus(""); setFRecon(""); setFSearch(""); setFPreset(""); try { localStorage.removeItem(FILTERS_KEY); } catch { /* ignore */ } };

  const DATE_PRESETS = [
    { value: "today", label: "Aujourd'hui" },
    { value: "yesterday", label: "Hier" },
    { value: "7d", label: "Derniers 7 jours" },
    { value: "30d", label: "Derniers 30 jours" },
    { value: "thisMonth", label: "Mois courant" },
    { value: "lastMonth", label: "Mois dernier" },
    { value: "lastYear", label: "L'année dernière" },
    { value: "last2Years", label: "Les deux dernières années" },
    { value: "thisYear", label: "Année en cours" },
  ];
  const applyPreset = (v) => {
    setFPreset(v);
    if (!v) return;
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth();
    let from = "", to = "";
    if (v === "today") { from = fmt(now); to = fmt(now); }
    else if (v === "yesterday") { const d = new Date(now); d.setDate(d.getDate() - 1); from = fmt(d); to = fmt(d); }
    else if (v === "7d") { const d = new Date(now); d.setDate(d.getDate() - 6); from = fmt(d); to = fmt(now); }
    else if (v === "30d") { const d = new Date(now); d.setDate(d.getDate() - 29); from = fmt(d); to = fmt(now); }
    else if (v === "thisMonth") { from = fmt(new Date(y, m, 1)); to = fmt(new Date(y, m + 1, 0)); }
    else if (v === "lastMonth") { from = fmt(new Date(y, m - 1, 1)); to = fmt(new Date(y, m, 0)); }
    else if (v === "lastYear") { from = fmt(new Date(y - 1, 0, 1)); to = fmt(new Date(y - 1, 11, 31)); }
    else if (v === "last2Years") { from = fmt(new Date(y - 2, 0, 1)); to = fmt(new Date(y - 1, 11, 31)); }
    else if (v === "thisYear") { from = fmt(new Date(y, 0, 1)); to = fmt(new Date(y, 11, 31)); }
    setFFrom(from); setFTo(to);
  };

  const isArrears = (t) => t.type === "Revenu" && (t.status === "En attente" || t.status === "Partiel");
  const resteAEncaisser = transactions
    .filter(isArrears)
    .reduce((s, t) => s + Math.max(0, (t.amount ?? 0) - (t.realized ?? 0)), 0);
  const arrearsCount = transactions.filter(isArrears).length;

  const remind = async (t) => {
    setRemindingId(t.id);
    try {
      const res = await api.post(`/transactions/${t.id}/remind`);
      toast.success(res.data?.status === "sent" ? `Relance envoyée à ${res.data.to}` : `Relance (simulée) à ${res.data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Relance impossible"); }
    finally { setRemindingId(null); }
  };

  const exportCsv = () => {
    const head = ["Date", "Bien", "Locataire", "Type", "Catégorie", "Description", "Montant", "Statut", "Réalisé", "Source"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const fmtAmt = (v, type) => {
      const n = Math.abs(Number(v) || 0) * (type === "Dépense" ? -1 : 1);
      return n.toFixed(2).replace(".", ",");
    };
    const lines = [head.join(";")].concat(filtered.map((t) => [t.date, canonProp(t.property), canonTenant(t.tenant), t.type, t.category, t.label, fmtAmt(t.amount, t.type), t.status || "Payé", fmtAmt(t.realized ?? t.amount, t.type), t.source || "manuel"].map(esc).join(";")));
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `finances-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success(`${filtered.length} écriture(s) exportée(s)`);
  };

  const deleteTx = async (id) => {
    if (!window.confirm("Supprimer cette écriture ?")) return;
    try { await api.delete(`/transactions/${id}`); setTransactions((p) => p.filter((t) => t.id !== id)); setSelectedTx((s) => s.filter((x) => x !== id)); toast.success("Écriture supprimée"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const toggleSelTx = (id) => setSelectedTx((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allFilteredSelected = filtered.length > 0 && filtered.every((t) => selSet.has(t.id));
  const toggleSelAllTx = () => setSelectedTx(allFilteredSelected ? [] : filtered.map((t) => t.id));
  const bulkDeleteTx = async () => {
    if (!selectedTx.length) return;
    setBulkDeleting(true);
    try {
      const res = await api.post("/transactions/bulk-delete", { ids: selectedTx });
      const ids = new Set(selectedTx);
      setTransactions((p) => p.filter((t) => !ids.has(t.id)));
      toast.success(`${res.data.deleted} écriture(s) supprimée(s)`);
      setSelectedTx([]);
      setConfirmBulk(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de la suppression"); }
    finally { setBulkDeleting(false); }
  };

  const changeStatus = async (t, status) => {
    if (status === t.status) return;
    if (status === "Partiel") { setPartialTx(t); return; }
    try {
      const res = await api.put(`/transactions/${t.id}/status`, { status });
      setTransactions((p) => p.map((x) => (x.id === t.id ? res.data : x)));
      toast.success(`Statut : ${status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6">
      <PayoutBanner />
      <div className="flex flex-wrap justify-end gap-2">
        <FinancesGuide />
        <AnnualReportButton />
        {finEdit ? (
          <>
            <button onClick={() => setFinEdit(false)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-green/70 font-semibold text-sm transition-colors" data-testid="fin-cancel-btn">Annuler</button>
            <button onClick={saveFinPrefs} disabled={savingFinPrefs} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm transition-colors disabled:opacity-60" data-testid="fin-save-btn">
              {savingFinPrefs ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Terminer
            </button>
          </>
        ) : (
          <button onClick={() => setFinEdit(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm transition-colors" data-testid="fin-customize-btn">
            <Settings2 className="w-4 h-4" /> Personnaliser
          </button>
        )}
        <button onClick={() => navigate("/app/finances/bilan")} data-testid="bilan-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm transition-colors">
          <FileBarChart className="w-4 h-4" /> Bilan &amp; Prévisionnel
        </button>
        <button onClick={() => navigate("/app/finances/prets")} data-testid="prets-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm transition-colors">
          <Landmark className="w-4 h-4" /> Prêts
        </button>
        <button onClick={() => navigate("/app/finances/rapprochement")} data-testid="rapprochement-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm transition-colors">
          <Banknote className="w-4 h-4" /> Rapprochement
        </button>
        <div className="relative">
          <button onClick={() => setAddMenu((v) => !v)} data-testid="add-transaction-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors">
            <Plus className="w-4 h-4" /> Ajouter <ChevronDown className="w-4 h-4" />
          </button>
          {addMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setAddMenu(false)} />
              <div className="absolute right-0 mt-1 w-60 bg-white rounded-xl border border-brand-soft shadow-xl z-50 py-1.5" data-testid="add-menu">
                <AddItem icon={ArrowUpRight} label="Ajouter un revenu" onClick={() => openTx("Revenu")} testid="menu-add-revenu" />
                <AddItem icon={ArrowDownRight} label="Ajouter une dépense" onClick={() => openTx("Dépense")} testid="menu-add-depense" />
                <AddItem icon={ScanLine} label="Scanner une ou plusieurs factures" onClick={() => scanRef.current?.click()} testid="menu-scan-invoice" />
                <AddItem icon={Repeat} label="Programmer une charge récurrente" onClick={() => { setAddMenu(false); setRecurringOpen(true); }} testid="menu-recurring" />
                <AddItem icon={FileSpreadsheet} label="Import de l'historique" onClick={() => { setAddMenu(false); setImportOpen(true); }} testid="menu-import-history" />
              </div>
            </>
          )}
          <input ref={scanRef} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={onScanFile} data-testid="scan-file-input" />
        </div>
      </div>

      {scanning && (
        <div className="bg-white rounded-2xl p-4 border border-brand-soft flex items-center gap-3 text-sm text-brand-green/70" data-testid="scanning-banner">
          <Loader2 className="w-5 h-5 animate-spin text-brand-limeDark" /> Analyse de la facture en cours (IA)…
        </div>
      )}

      <div className="bg-white rounded-2xl border border-brand-soft p-4" data-testid="finance-filters-top">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-green/40" />
            <input value={fSearch} onChange={(e) => setFSearch(e.target.value)} placeholder="Libellé, bien, locataire, montant…" className="fsel !max-w-[230px] pl-8 pr-7" data-testid="filter-search" />
            {fSearch && <button onClick={() => setFSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-brand-green/50 hover:text-brand-greenDeep hover:bg-brand-softer" title="Effacer" data-testid="filter-search-clear"><X className="w-3.5 h-3.5" /></button>}
          </div>
          {qSearch && <span className={`text-xs font-semibold ${fSearch !== dSearch ? "text-brand-green/40" : "text-brand-greenDeep"}`} data-testid="filter-search-count">{filtered.length} résultat{filtered.length > 1 ? "s" : ""}</span>}
          <span className="text-brand-green/40"><Filter className="w-4 h-4" /></span>
          <select value={fType} onChange={(e) => setFType(e.target.value)} className="fsel" data-testid="filter-type">
            <option value="">Revenus & dépenses</option>
            <option value="Revenu">Revenus</option>
            <option value="Dépense">Dépenses</option>
          </select>
          <select value={fBien} onChange={(e) => setFBien(e.target.value)} className="fsel" data-testid="filter-bien">
            <option value="">Tous les biens</option>
            {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
          <select value={fCat} onChange={(e) => setFCat(e.target.value)} className="fsel" data-testid="filter-category">
            <option value="">Toutes catégories</option>
            {[...cats.revenue, ...cats.expense].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="fsel" data-testid="filter-status">
            <option value="">Tous les statuts</option>
            {["Payé", "En attente", "Partiel", "Perdu", "Non payé"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fRecon} onChange={(e) => setFRecon(e.target.value)} className="fsel" data-testid="filter-reconciled">
            <option value="">Rapprochement : tous</option>
            <option value="oui">Rapproché</option>
            <option value="non">Non rapproché</option>
          </select>
          <select value={fPreset} onChange={(e) => applyPreset(e.target.value)} className="fsel" title="Période rapide" data-testid="filter-date-preset">
            <option value="">Période…</option>
            {DATE_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <input type="date" value={fFrom} onChange={(e) => { setFFrom(e.target.value); setFPreset(""); }} className="fsel" title="Du" data-testid="filter-from" />
          <input type="date" value={fTo} onChange={(e) => { setFTo(e.target.value); setFPreset(""); }} className="fsel" title="Au" data-testid="filter-to" />
          {filtersActive && <button onClick={clearFilters} className="text-xs text-brand-green/50 hover:text-brand-greenDeep" data-testid="filter-clear">Réinitialiser</button>}
        </div>
      </div>

      {finEdit ? (
        <>
          <p className="text-sm text-brand-green/55 bg-brand-lime/10 border border-brand-lime/30 rounded-xl px-4 py-3" data-testid="fin-edit-hint">Glissez les sections par la poignée pour les réorganiser, ou masquez/affichez-les. Cliquez sur « Terminer » pour enregistrer.</p>
          <Reorder.Group axis="y" values={finOrder} onReorder={setFinOrder} className="space-y-4">
            {finOrder.map((key) => (
              <FinDraggable key={key} blockKey={key} hidden={finHidden.includes(key)} onToggle={() => toggleFinSection(key)}>
                {renderFinSection(key)}
              </FinDraggable>
            ))}
          </Reorder.Group>
        </>
      ) : (
        <div className="space-y-6">
          {finOrder.filter((k) => !finHidden.includes(k)).map((key) => (
            <div key={key} data-testid={`fin-section-${key}`}>{renderFinSection(key)}</div>
          ))}
        </div>
      )}

      {fiscalOpen && <FiscalModal bien={fBien} onClose={() => setFiscalOpen(false)} />}
      {multiScan && (
        <MultiScanModal
          rows={multiScan.rows} properties={properties} cats={cats}
          onClose={() => setMultiScan(null)}
          onDone={(created) => { loadTx(); setMultiScan(null); toast.success(`${created} dépense(s) créée(s)`); }}
        />
      )}
      {recurringOpen && (
        <RecurringModal properties={properties} owners={owners} cats={cats}
          onClose={() => setRecurringOpen(false)} onChange={loadTx} />
      )}
      {importOpen && (
        <ImportHistoryModal onClose={() => setImportOpen(false)} onImported={loadTx} properties={properties} cats={cats} />
      )}
      {partialTx && (
        <PartialModal tx={partialTx} onClose={() => setPartialTx(null)}
          onSaved={(updated) => { setTransactions((p) => p.map((x) => (x.id === updated.id ? updated : x))); setPartialTx(updated.status === "Partiel" ? updated : null); }} />
      )}
      {confirmBulk && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => !bulkDeleting && setConfirmBulk(false)}>
          <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="bulk-delete-modal">
            <div className="p-6 pb-4 border-b border-brand-soft flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center shrink-0"><Trash2 className="w-5 h-5 text-red-500" /></span>
              <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Supprimer {selectedTx.length} écriture(s) ?</h3>
            </div>
            <div className="p-6 pt-5 space-y-4">
              <p className="text-sm text-brand-green/70">Cette action est définitive. Les écritures sélectionnées (y compris celles générées automatiquement) seront supprimées. Les quittances liées repasseront en « En attente ».</p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmBulk(false)} disabled={bulkDeleting} className="flex-1 py-3 rounded-full border border-brand-soft text-brand-greenDeep font-semibold hover:bg-brand-softer transition-colors disabled:opacity-60" data-testid="bulk-delete-cancel">Annuler</button>
                <button onClick={bulkDeleteTx} disabled={bulkDeleting} className="flex-1 py-3 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60" data-testid="bulk-delete-confirm">
                  {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`.fsel{padding:0.4rem 0.7rem;border-radius:0.7rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.8rem;max-width:180px}.fsel:focus{border-color:#c9a24b}.status-sel{padding:0.3rem 0.6rem;border-radius:999px;border:none;outline:none;font-size:0.72rem;font-weight:600;cursor:pointer;-webkit-appearance:none;appearance:none}`}</style>
      {txModal && (
        <TransactionModal
          state={txModal} setState={setTxModal} owners={owners} properties={properties} leases={leases} cats={cats}
          onClose={() => setTxModal(null)}
          onSaved={(saved, editId) => {
            setTransactions((p) => editId ? p.map((t) => (t.id === editId ? saved : t)) : [saved, ...p]);
            setTxModal(null);
          }}
        />
      )}
    </div>
  );
}

function AddItem({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left text-brand-greenDeep hover:bg-brand-softer transition-colors">
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function MultiScanModal({ rows, properties, cats, onClose, onDone }) {
  const [items, setItems] = useState(rows);
  const [busy, setBusy] = useState(false);
  const setRow = (i, k, v) => setItems((arr) => arr.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const removeRow = (i) => setItems((arr) => arr.filter((_, j) => j !== i));
  const createAll = async () => {
    setBusy(true);
    let created = 0;
    for (const r of items) {
      if (!r.amount || Number(r.amount) <= 0 || !r.category) continue;
      try {
        await api.post("/transactions", {
          type: "Dépense", category: r.category, owner: r.owner || "", property: r.property || "",
          lease: r.lease || "", amount: Number(r.amount) || 0, date: r.date || "", label: r.label || "",
          document_url: r.document_url || "",
        });
        created += 1;
      } catch { /* skip */ }
    }
    setBusy(false);
    onDone(created);
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="multiscan-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><ScanLine className="w-5 h-5" /> Relecture des factures ({items.length})</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 pt-5 space-y-3">
          <p className="text-sm text-brand-green/60">Vérifiez et corrigez chaque dépense détectée avant de créer.</p>
          {items.map((r, i) => (
            <div key={i} className="rounded-2xl border border-brand-soft p-4 space-y-3" data-testid={`multiscan-row-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-brand-green/50 flex items-center gap-2 truncate">{r.document_url && <Paperclip className="w-3.5 h-3.5" />}{r.filename}{r.error && <span className="text-red-500">· non lu, à saisir</span>}</span>
                <button onClick={() => removeRow(i)} className="p-1 rounded-full hover:bg-red-50 text-brand-green/40 hover:text-red-600" data-testid={`multiscan-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <select value={r.category} onChange={(e) => setRow(i, "category", e.target.value)} className="inp" data-testid={`multiscan-category-${i}`}>
                  <option value="">— Catégorie —</option>
                  {cats.expense.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={r.property} onChange={(e) => setRow(i, "property", e.target.value)} className="inp" data-testid={`multiscan-property-${i}`}>
                  <option value="">— Bien (optionnel) —</option>
                  {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                  <option value="Pas lié à un bien">Pas lié à un bien</option>
                </select>
                <input type="number" step="0.01" value={r.amount} onChange={(e) => setRow(i, "amount", e.target.value)} placeholder="Montant €" className="inp" data-testid={`multiscan-amount-${i}`} />
                <input type="date" value={r.date} onChange={(e) => setRow(i, "date", e.target.value)} className="inp" data-testid={`multiscan-date-${i}`} />
                <input value={r.label} onChange={(e) => setRow(i, "label", e.target.value)} placeholder="Libellé / fournisseur" className="inp sm:col-span-2" data-testid={`multiscan-label-${i}`} />
              </div>
            </div>
          ))}
          <button onClick={createAll} disabled={busy || !items.length} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="multiscan-create-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Créer {items.length} dépense{items.length > 1 ? "s" : ""}
          </button>
        </div>
        <style>{`.inp{width:100%;padding:0.55rem 0.85rem;border-radius:0.8rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.85rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
      </div>
    </div>
  );
}

function TransactionModal({ state, setState, owners, properties, leases, cats, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const docRef = useRef(null);
  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const isRevenu = state.type === "Revenu";
  const categoryList = isRevenu ? cats.revenue : cats.expense;
  const leaseOptions = leases.filter((l) => !state.property || state.property === NO_PROPERTY ? false : l.property === state.property);

  const onProperty = (v) => setState((s) => ({ ...s, property: v, lease: v === NO_PROPERTY ? NO_LEASE : "" }));

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (docRef.current) docRef.current.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/tenants/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("document_url", res.data.url);
      toast.success("Document joint");
    } catch { toast.error("Échec de l'upload"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!state.amount || Number(state.amount) <= 0) return toast.error("Montant requis");
    if (!state.category) return toast.error("Sélectionnez un type");
    setBusy(true);
    try {
      const payload = {
        type: state.type, category: state.category, owner: state.owner || "",
        property: state.property || "", lease: state.lease || "", tenant: state.tenant || "",
        amount: Number(state.amount) || 0, date: state.date || "", label: state.label || "",
        document_url: state.document_url || "", status: state.status || "Payé",
        unpaid_reason: state.status === "Non payé" ? (state.unpaid_reason || "") : "",
      };
      if (state.editId) payload.reconciled = !!state.reconciled;
      const res = state.editId ? await api.put(`/transactions/${state.editId}`, payload) : await api.post("/transactions", payload);
      toast.success(state.editId ? "Écriture mise à jour" : `${state.type} ajouté(e)`);
      onSaved(res.data, state.editId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="transaction-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep">{state.editId ? "Modifier l'écriture" : isRevenu ? "Ajouter un revenu" : "Ajouter une dépense"}</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 pt-5 space-y-5">
          <div className="flex gap-2 p-1 bg-brand-softer rounded-full">
            <button onClick={() => set("type", "Dépense")} className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${!isRevenu ? "bg-white shadow text-orange-600" : "text-brand-green/60"}`} data-testid="tx-type-depense">Dépense</button>
            <button onClick={() => set("type", "Revenu")} className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${isRevenu ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/60"}`} data-testid="tx-type-revenu">Revenu</button>
          </div>

          <div>
            <h4 className="font-semibold text-brand-greenDeep text-sm border-b border-brand-soft pb-2 mb-3">Affectation</h4>
            <div className="space-y-3">
              <Field label="Propriétaire">
                <select value={state.owner} onChange={(e) => set("owner", e.target.value)} className="inp" data-testid="tx-owner-select">
                  <option value="">— Sélectionner —</option>
                  {owners.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
                </select>
              </Field>
              <Field label="Bien">
                <select value={state.property} onChange={(e) => onProperty(e.target.value)} className="inp" data-testid="tx-property-select">
                  <option value="">— Sélectionner —</option>
                  {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                  <option value={NO_PROPERTY}>{NO_PROPERTY}</option>
                </select>
              </Field>
              <Field label="Location">
                <select value={state.lease} onChange={(e) => set("lease", e.target.value)} className="inp" disabled={state.property === NO_PROPERTY} data-testid="tx-lease-select">
                  <option value="">— Sélectionner —</option>
                  {leaseOptions.map((l) => <option key={l.id} value={l.reference || l.id}>{[l.tenant, l.reference].filter(Boolean).join(" · ") || l.id}</option>)}
                  <option value={NO_LEASE}>{NO_LEASE}</option>
                </select>
              </Field>
              <Field label="Locataire">
                <input value={state.tenant} onChange={(e) => set("tenant", e.target.value)} placeholder="Auto d'après la location" className="inp" data-testid="tx-tenant-input" />
              </Field>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-brand-greenDeep text-sm border-b border-brand-soft pb-2 mb-3">Informations générales</h4>
            <div className="space-y-3">
              <Field label="Type">
                <select value={state.category} onChange={(e) => set("category", e.target.value)} className="inp" data-testid="tx-category-select">
                  <option value="">— Sélectionner —</option>
                  {categoryList.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Montant (€)">
                  <input type="number" step="0.01" min="0" value={state.amount} onChange={(e) => set("amount", e.target.value)} className="inp" data-testid="tx-amount-input" />
                </Field>
                <Field label="Date">
                  <input type="date" value={state.date} onChange={(e) => set("date", e.target.value)} className="inp" data-testid="tx-date-input" />
                </Field>
              </div>
              <Field label="Libellé (optionnel)">
                <input value={state.label} onChange={(e) => set("label", e.target.value)} placeholder="Ex. fournisseur, référence…" className="inp" data-testid="tx-label-input" />
              </Field>
              <Field label="Statut">
                <select value={state.status} onChange={(e) => set("status", e.target.value)} className="inp" data-testid="tx-status-select">
                  {(state.source === "quittance" && state.status !== "Payé" ? ["Non payé", "Perdu"] : ["Payé", "En attente", "Partiel", "Perdu", "Non payé"]).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              {state.status === "Non payé" && (
                <Field label="Motif de l'impayé">
                  <textarea value={state.unpaid_reason || ""} onChange={(e) => set("unpaid_reason", e.target.value)} rows={3} placeholder="Expliquez l'impayé (ex. locataire en difficulté, litige, chèque sans provision…). Si le loyer est définitivement perdu, passez le statut en « Perdu »." className="inp resize-none" data-testid="tx-unpaid-reason" />
                  <p className="text-[11px] text-brand-green/45 mt-1">Cette écriture « Non payé » n'est comptée dans aucun total (revenus fonciers, résultat net, montants par catégorie).</p>
                </Field>
              )}
              {state.editId && (
                <Field label="Écriture rapprochée">
                  <select value={state.reconciled ? "oui" : "non"} onChange={(e) => set("reconciled", e.target.value === "oui")} className="inp" data-testid="tx-reconciled-select">
                    <option value="non">Non</option>
                    <option value="oui">Oui</option>
                  </select>
                  <p className="text-[11px] text-brand-green/45 mt-1">Passer à « Non » détache et dépointe l'opération bancaire liée dans le rapprochement.</p>
                </Field>
              )}
              <Field label="Document (facture, justificatif)">
                {state.document_url ? (
                  <div className="flex items-center gap-2">
                    <a href={imgSrc(state.document_url)} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-brand-soft text-sm text-brand-greenDeep bg-brand-softer truncate" data-testid="tx-doc-link"><Paperclip className="w-4 h-4" /> Document joint</a>
                    <button onClick={() => set("document_url", "")} className="p-2 rounded-xl border border-brand-soft text-brand-green/50 hover:text-red-600"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <button onClick={() => docRef.current?.click()} disabled={uploading} className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 border-dashed border-brand-soft text-sm text-brand-green/60 hover:border-brand-limeDark" data-testid="tx-doc-upload-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Joindre un document (image ou PDF)
                  </button>
                )}
                <input ref={docRef} type="file" accept="image/*,.pdf" className="hidden" onChange={uploadDoc} data-testid="tx-doc-file-input" />
              </Field>
            </div>
          </div>

          <button onClick={save} disabled={busy} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="tx-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {state.editId ? "Enregistrer" : "Ajouter"}
          </button>
        </div>
      </div>
      <style>{`.inp{width:100%;padding:0.6rem 0.9rem;border-radius:0.9rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.9rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}.inp:disabled{opacity:.5}`}</style>
    </div>
  );
}

function RecurringModal({ properties, owners, cats, onClose, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const emptyForm = { category: "", owner: "", property: "", tenant: "", amount: "", label: "", frequency: "Annuelle", start_date: new Date().toISOString().slice(0, 10), active: true };
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/recurring-expenses").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(emptyForm); setEditId(null); };

  const save = async () => {
    if (!form.category) return toast.error("Sélectionnez une catégorie");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Montant requis");
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editId) {
        await api.put(`/recurring-expenses/${editId}`, payload);
        toast.success("Charge modifiée · écritures générées mises à jour");
      } else {
        await api.post("/recurring-expenses", payload);
        toast.success("Charge récurrente programmée · échéances générées");
      }
      resetForm();
      await load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const startEdit = (it) => {
    setEditId(it.id);
    setForm({
      category: it.category || "", owner: it.owner || "", property: it.property || "", tenant: it.tenant || "",
      amount: it.amount ?? "", label: it.label || "", frequency: it.frequency || "Annuelle",
      start_date: it.start_date || new Date().toISOString().slice(0, 10), active: it.active !== false,
    });
  };

  const toggle = async (it) => {
    try { await api.put(`/recurring-expenses/${it.id}`, { ...it, active: !it.active }); await load(); onChange?.(); }
    catch { toast.error("Erreur"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette charge récurrente ? (les dépenses déjà générées sont conservées)")) return;
    try { await api.delete(`/recurring-expenses/${id}`); if (editId === id) resetForm(); await load(); toast.success("Supprimée"); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="recurring-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><Repeat className="w-5 h-5" /> Charges récurrentes</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 pt-5 space-y-5">
          <p className="text-sm text-brand-green/60">Programmez une dépense qui se répète automatiquement (ex. taxe foncière annuelle, assurance). Les échéances passées et à venir sont générées automatiquement. Le bien, la description et le bénéficiaire sont repris dans « Dernières transactions ».</p>

          <div className={`rounded-2xl border p-4 space-y-3 ${editId ? "border-brand-limeDark bg-brand-lime/5" : "border-brand-soft"}`}>
            {editId && <p className="text-xs font-semibold text-brand-limeDark flex items-center gap-1.5" data-testid="rec-edit-banner"><Pencil className="w-3.5 h-3.5" /> Modification — toute modification du bien, de la description ou du bénéficiaire sera appliquée aux écritures déjà générées.</p>}
            <div className="grid sm:grid-cols-2 gap-3">
              <select value={form.category} onChange={(e) => set("category", e.target.value)} className="inp" data-testid="rec-category">
                <option value="">— Catégorie —</option>
                {cats.expense.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={form.property} onChange={(e) => set("property", e.target.value)} className="inp" data-testid="rec-property">
                <option value="">— Bien (optionnel) —</option>
                {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                <option value="Pas lié à un bien">Pas lié à un bien</option>
              </select>
              <input type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="Montant €" className="inp" data-testid="rec-amount" />
              <select value={form.frequency} onChange={(e) => set("frequency", e.target.value)} className="inp" data-testid="rec-frequency">
                <option value="Annuelle">Annuelle</option>
                <option value="Trimestrielle">Trimestrielle</option>
                <option value="Mensuelle">Mensuelle</option>
              </select>
              <input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="Description (ex. Taxe foncière)" className="inp" data-testid="rec-label" />
              <input value={form.tenant} onChange={(e) => set("tenant", e.target.value)} placeholder="Bénéficiaire / locataire" className="inp" data-testid="rec-tenant" />
              <label className="flex flex-col text-xs text-brand-green/50">Première échéance
                <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="inp mt-1" data-testid="rec-start" />
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={save} disabled={busy} className="flex-1 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="rec-add-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />)} {editId ? "Enregistrer les modifications" : "Programmer"}
              </button>
              {editId && <button onClick={resetForm} disabled={busy} className="px-4 py-2.5 rounded-full border border-brand-soft text-brand-green/70 font-semibold text-sm hover:bg-brand-softer" data-testid="rec-cancel-btn">Annuler</button>}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-brand-green/70 mb-2">Charges programmées</p>
            {loading ? <p className="text-sm text-brand-green/50 py-3 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></p>
              : items.length === 0 ? <p className="text-sm text-brand-green/45 py-3 text-center bg-brand-softer/50 rounded-xl" data-testid="recurring-empty">Aucune charge récurrente.</p>
              : (
                <div className="space-y-2" data-testid="recurring-list">
                  {items.map((it) => (
                    <div key={it.id} className={`flex items-center justify-between p-3 rounded-xl border ${editId === it.id ? "border-brand-limeDark bg-brand-lime/5" : "border-brand-soft"}`} data-testid={`rec-item-${it.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-brand-greenDeep truncate">{it.label || it.category}</p>
                        <p className="text-xs text-brand-green/50 truncate">{[it.frequency, it.property, it.tenant, `depuis ${it.start_date}`, `${(it.amount || 0).toLocaleString("fr-FR")} €`].filter(Boolean).join(" • ")}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => startEdit(it)} className="p-1.5 rounded-full hover:bg-brand-softer text-brand-green/50 hover:text-brand-greenDeep" data-testid={`rec-edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => toggle(it)} className={`text-xs px-2.5 py-1 rounded-full font-semibold ${it.active ? "bg-brand-lime/30 text-brand-greenDeep" : "bg-brand-soft text-brand-green/50"}`} data-testid={`rec-toggle-${it.id}`}>{it.active ? "Active" : "En pause"}</button>
                        <button onClick={() => remove(it.id)} className="p-1.5 rounded-full hover:bg-red-50 text-brand-green/50 hover:text-red-600" data-testid={`rec-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
        <style>{`.inp{width:100%;padding:0.55rem 0.85rem;border-radius:0.8rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.85rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-brand-green/70 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function PartialModal({ tx, onClose, onSaved }) {
  const paid = tx.paid || 0;
  const remaining = Math.max(0, (tx.amount || 0) - paid);
  const [amount, setAmount] = useState(remaining || "");
  const [method, setMethod] = useState("Virement");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [editIdx, setEditIdx] = useState(null); // index of versement being edited
  const [edit, setEdit] = useState({ amount: "", method: "Virement", date: "" });
  const [rowBusy, setRowBusy] = useState(null);
  const fmt = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const add = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Montant requis");
    setBusy(true);
    try {
      const res = await api.post(`/transactions/${tx.id}/payments`, { amount: Number(amount), method, date });
      toast.success(res.data.status === "Payé" ? "Paiement complet — écriture réglée" : "Versement enregistré");
      onSaved(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const startEdit = (p, i) => {
    setEditIdx(i);
    setEdit({ amount: p.amount ?? "", method: p.method || "Virement", date: p.date || new Date().toISOString().slice(0, 10) });
  };

  const saveEdit = async (i) => {
    if (!edit.amount || Number(edit.amount) <= 0) return toast.error("Montant requis");
    setRowBusy(i);
    try {
      const res = await api.put(`/transactions/${tx.id}/payments/${i}`, { amount: Number(edit.amount), method: edit.method, date: edit.date });
      toast.success("Versement corrigé");
      setEditIdx(null);
      onSaved(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setRowBusy(null); }
  };

  const removePayment = async (i) => {
    if (!window.confirm("Supprimer ce versement ?")) return;
    setRowBusy(i);
    try {
      const res = await api.delete(`/transactions/${tx.id}/payments/${i}`);
      toast.success("Versement supprimé");
      setEditIdx(null);
      onSaved(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setRowBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="partial-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Paiement partiel</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 pt-5 space-y-4">
          <p className="text-sm text-brand-green/60 truncate">{tx.label}</p>
          <div className="rounded-2xl bg-brand-softer p-4 text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-brand-green/60">Montant total</span><b>{fmt(tx.amount)} €</b></div>
            <div className="flex justify-between"><span className="text-brand-green/60">Déjà encaissé</span><b className="text-brand-greenDeep">{fmt(paid)} €</b></div>
            <div className="flex justify-between border-t border-brand-soft pt-1.5"><span className="text-brand-green/60">Reste dû</span><b className="text-orange-600" data-testid="partial-remaining">{fmt(remaining)} €</b></div>
            <div className="h-1.5 rounded-full bg-brand-soft overflow-hidden mt-1"><div className="h-full bg-brand-limeDark" style={{ width: `${Math.min(100, (paid / (tx.amount || 1)) * 100)}%` }} /></div>
          </div>
          {(tx.payments || []).length > 0 && (
            <div className="space-y-1.5" data-testid="partial-history">
              <p className="text-xs font-medium text-brand-green/60">Versements</p>
              {tx.payments.map((p, i) => (
                editIdx === i ? (
                  <div key={i} className="rounded-lg bg-brand-softer/60 p-2.5 space-y-2" data-testid={`partial-edit-row-${i}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" step="0.01" min="0" value={edit.amount} onChange={(e) => setEdit((s) => ({ ...s, amount: e.target.value }))} className="inp" placeholder="Montant" data-testid={`partial-edit-amount-${i}`} />
                      <input type="date" value={edit.date} onChange={(e) => setEdit((s) => ({ ...s, date: e.target.value }))} className="inp" data-testid={`partial-edit-date-${i}`} />
                    </div>
                    <select value={edit.method} onChange={(e) => setEdit((s) => ({ ...s, method: e.target.value }))} className="inp" data-testid={`partial-edit-method-${i}`}>
                      {["Chèque", "Virement", "Espèces"].map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditIdx(null)} className="text-xs px-3 py-1.5 rounded-full border border-brand-soft text-brand-green/60 hover:bg-brand-soft" data-testid={`partial-edit-cancel-${i}`}>Annuler</button>
                      <button onClick={() => saveEdit(i)} disabled={rowBusy === i} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold disabled:opacity-60" data-testid={`partial-edit-save-${i}`}>
                        {rowBusy === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Enregistrer
                      </button>
                    </div>
                  </div>
                ) : (
                  <div key={i} className="flex items-center justify-between text-xs text-brand-green/70 px-3 py-2 rounded-lg bg-brand-softer/60 group" data-testid={`partial-payment-${i}`}>
                    <span>{p.date ? new Date(p.date).toLocaleDateString("fr-FR") : ""} · {p.method}</span>
                    <span className="flex items-center gap-2">
                      <b>{fmt(p.amount)} €</b>
                      <button onClick={() => startEdit(p, i)} className="p-1 rounded-full hover:bg-brand-soft text-brand-green/40 hover:text-brand-greenDeep" title="Corriger" data-testid={`partial-edit-btn-${i}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => removePayment(i)} disabled={rowBusy === i} className="p-1 rounded-full hover:bg-red-50 text-brand-green/40 hover:text-red-600 disabled:opacity-60" title="Supprimer" data-testid={`partial-delete-btn-${i}`}>{rowBusy === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}</button>
                    </span>
                  </div>
                )
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Montant du versement (€)"><input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="inp" data-testid="partial-amount" /></Field>
            <Field label="Date"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="inp" data-testid="partial-date" /></Field>
          </div>
          <Field label="Moyen de paiement">
            <select value={method} onChange={(e) => setMethod(e.target.value)} className="inp" data-testid="partial-method">
              {["Chèque", "Virement", "Espèces"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <button onClick={add} disabled={busy} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="partial-add-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Enregistrer le versement
          </button>
        </div>
        <style>{`.inp{width:100%;padding:0.55rem 0.85rem;border-radius:0.8rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.85rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>
      </div>
    </div>
  );
}

function FiscalModal({ onClose, bien = "" }) {
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  const years = [now, now - 1, now - 2, now - 3];
  const propParam = bien ? `&property=${encodeURIComponent(bien)}` : "";
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="fiscal-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><FileBarChart className="w-5 h-5" /> Attestation fiscale</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 pt-5">
          <p className="text-sm text-brand-green/60 mb-4">Générez une attestation des loyers perçus sur l'année, prête à joindre à votre déclaration de revenus fonciers (2044).</p>
          {bien && <p className="text-xs px-3 py-2 rounded-lg bg-brand-softer text-brand-greenDeep mb-4" data-testid="fiscal-filter-info">Filtré sur le bien : <b>{bien}</b></p>}
          <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Année fiscale</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-full px-4 py-2.5 rounded-xl border border-brand-soft outline-none focus:border-brand-limeDark mb-5" data-testid="fiscal-year-select">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => { window.open(`${BACKEND_URL}/api/fiscal/attestation-pdf?year=${year}${propParam}`, "_blank"); onClose(); }} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2" data-testid="download-fiscal-btn">
            <Download className="w-4 h-4" /> Télécharger l'attestation {year}
          </button>
        </div>
      </div>
    </div>
  );
}
